import React, { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Link as LinkIcon, X, Loader2, FileText, ExternalLink } from "lucide-react";
import { resolveImageUrl } from "./ImageUpload";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp";

const DocumentUpload = ({ value, onChange, placeholder = "https://… ou téléverser un document" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const href = resolveImageUrl(value);
  const fileName = (value || "").split("/").pop();

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Document trop volumineux (max 15 Mo)");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload-document", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Document téléversé");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du téléversement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid="document-upload">
      <div className="flex items-center gap-2">
        <LinkIcon size={14} className="text-stone-400 shrink-0" />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid="document-upload-url"
          className="flex-1 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
        />
      </div>
      {value && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="document-upload-preview"
          className="inline-flex items-center gap-1.5 text-xs text-deep-rose hover:underline max-w-full"
        >
          <FileText size={13} className="shrink-0" />
          <span className="truncate">{fileName || "Voir le document"}</span>
          <ExternalLink size={11} className="shrink-0" />
        </a>
      )}
      <div className="flex gap-2">
        <input ref={fileRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden" />
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          data-testid="document-upload-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blush-light text-deep-rose text-xs font-medium disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Envoi..." : "Téléverser un document"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 text-stone-600 text-xs"
          >
            <X size={14} /> Effacer
          </button>
        )}
      </div>
      <p className="text-[11px] text-stone-400">PDF, Word, Excel ou image (max 15 Mo). Vous pouvez aussi coller une URL.</p>
    </div>
  );
};

export default DocumentUpload;
